import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import './ViewJobposting.css';
import 'bootstrap/dist/css/bootstrap.min.css';
import 'bootstrap-icons/font/bootstrap-icons.css';
import Navbar from '../../Pages/Navbar/EmployerNavbar';
import BaseUrl from '../../ApiServices/BaseUrl';
import Footer from "../../Footer/Footer";
import { Modal } from 'react-bootstrap';
import Swal from 'sweetalert2';
import { BsArrowRepeat, BsPencilSquare, BsTrash, BsHourglassSplit } from 'react-icons/bs';

function ViewJobs() {
    const navigate = useNavigate();
    const location = useLocation();
    const user = JSON.parse(localStorage.getItem('user')) || {};
    const userId = user?.id;
    const [jobs, setJobs] = useState([]);
    const [filteredJobs, setFilteredJobs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [deleteLoadingId, setDeleteLoadingId] = useState(null);
    const [deleteError, setDeleteError] = useState(null);
    const [repostingJobId, setRepostingJobId] = useState(null);
    const [currentFeatures, setCurrentFeatures] = useState([]);

    // Messages inbox state
    const [showMsgModal, setShowMsgModal] = useState(false);
    const [selectedMsgJob, setSelectedMsgJob] = useState(null);
    const [jobMessages, setJobMessages] = useState([]);
    const [msgLoading, setMsgLoading] = useState(false);
    const [selectedCandidate, setSelectedCandidate] = useState(null);
    const selectedCandidateRef = useRef(null);
    const [replyText, setReplyText] = useState('');
    const [replying, setReplying] = useState(false);
    const [msgCounts, setMsgCounts] = useState({});

    // Initial filter state
    const initialFilters = {
        location: [],
        position: '',
        startDate: '',
        jobType: '',
        contractStatus: [],
        postBy: '',
        language: [],
        skills: []
    };

    const [activeFilters, setActiveFilters] = useState(initialFilters);
    const [showAllPositions, setShowAllPositions] = useState(false);

    // Toggle states for filter sections - ALL CLOSED BY DEFAULT
    const [showLocation, setShowLocation] = useState(false);
    const [showPosition, setShowPosition] = useState(false);
    const [showStartDate, setShowStartDate] = useState(false);
    const [showJobType, setShowJobType] = useState(false);
    const [showContractStatus, setShowContractStatus] = useState(false);
    const [showPostBy, setShowPostBy] = useState(false);
    const [showLanguage, setShowLanguage] = useState(false);
    const [showSkills, setShowSkills] = useState(false);
    const [showFiltersMobile, setShowFiltersMobile] = useState(false);
    const [screenWidth, setScreenWidth] = useState(window.innerWidth);
    const [candidateHasSub, setCandidateHasSub] = useState(false);

    // Update screen width on resize
    const [employerSubPlan, setEmployerSubPlan] = useState('free');
    const [allowFreeCustomChat, setAllowFreeCustomChat] = useState(false);
    const [chatStatus, setChatStatus] = useState({ stepCount: 0, isLocked: false, nextSenderIsCandidate: true });

    const hasEmployerRepostAccess = () => {
        return currentFeatures.some(feature =>
            feature.feature_key === 'employer_job_repost_access' && feature.is_enabled
        );
    };

    const getRepostCooldownEnd = (item) => {
        const lastRepostedAt = item?.last_reposted_at || item?.reposted_at;
        if (!lastRepostedAt) return null;

        const nextAvailableAt = new Date(lastRepostedAt);
        if (Number.isNaN(nextAvailableAt.getTime())) return null;

        nextAvailableAt.setDate(nextAvailableAt.getDate() + 7);
        return nextAvailableAt;
    };

    const isInRepostCooldown = (item) => {
        const nextAvailableAt = getRepostCooldownEnd(item);
        return !!nextAvailableAt && nextAvailableAt > new Date();
    };

    const isPendingVerificationJob = (job) => {
        const status = String(job?.status || '').trim().toLowerCase();
        return status === 'pending verification' || status === 'pending';
    };

    useEffect(() => {
        const handleResize = () => setScreenWidth(window.innerWidth);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Auto-login logic from email link
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const emailFromUrl = params.get('email');

        if (emailFromUrl) {
            const autoLogin = async () => {
                try {
                    const response = await fetch(`${BaseUrl}/login-by-email`, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                        },
                        body: JSON.stringify({ email: emailFromUrl }),
                    });

                    if (response.ok) {
                        const data = await response.json();
                        // Store user in localStorage (matches LoginPage.js logic)
                        localStorage.setItem("user", JSON.stringify(data.user));
                        localStorage.setItem("userId", data.user.id);
                        localStorage.setItem("userPlan", data.user.plan_name || 'free');
                        localStorage.setItem("userStartDate", data.user.plan_startdate);
                        localStorage.setItem("userEnddate", data.user.plan_enddate);

                        // Clear URL parameters and reload to apply login state
                        window.history.replaceState({}, document.title, window.location.pathname);
                        window.location.reload();
                    } else {
                        console.error('Auto-login failed:', await response.json());
                    }
                } catch (error) {
                    console.error('Error during auto-login:', error);
                }
            };

            autoLogin();
        }
    }, [location]);

    const isMobile = screenWidth < 768; // Mobile breakpoint

    // States for showing more items
    const [showAllLocations, setShowAllLocations] = useState(false);
    const [showAllJobTypes, setShowAllJobTypes] = useState(false);
    const [showAllContractStatuses, setShowAllContractStatuses] = useState(false);
    const [showAllLanguages, setShowAllLanguages] = useState(false);
    const [showAllSkills, setShowAllSkills] = useState(false);

    // Data for filters
    const jobLocations = [
        { id: 'united-arab-emirates', name: 'United Arab Emirates (UAE)', icon: 'https://cdn.helperplace.com/country/united-arab-emirates.svg' },
        { id: 'dubai', name: 'Dubai', icon: 'https://cdn.helperplace.com/country/united-arab-emirates.svg' },
        { id: 'saudi-arabia', name: 'Saudi Arabia', icon: 'https://cdn.helperplace.com/country/saudi-arabia.svg' },
        { id: 'qatar', name: 'Qatar', icon: 'https://cdn.helperplace.com/country/qatar.svg' },
        { id: 'kuwait', name: 'Kuwait', icon: 'https://cdn.helperplace.com/country/kuwait.svg' },
        { id: 'bahrain', name: 'Bahrain', icon: 'https://flagcdn.com/w40/bh.png' },
        { id: 'oman', name: 'Oman', icon: 'https://cdn.helperplace.com/country/oman.svg' },
        { id: 'singapore', name: 'Singapore', icon: 'https://cdn.helperplace.com/country/singapore.svg' },
        { id: 'malaysia', name: 'Malaysia', icon: 'https://cdn.helperplace.com/country/malaysia.svg' },
        { id: 'hong-kong', name: 'Hong Kong', icon: 'https://cdn.helperplace.com/country/hong-kong.svg' },
        { id: 'japan', name: 'Japan', icon: 'https://flagcdn.com/w40/jp.png' },
        { id: 'south-korea', name: 'South Korea', icon: 'https://flagcdn.com/w40/kr.png' },
        { id: 'taiwan', name: 'Taiwan', icon: 'https://cdn.helperplace.com/country/taiwan.svg' },
        { id: 'germany', name: 'Germany', icon: 'https://flagcdn.com/w40/de.png' },
        { id: 'netherlands', name: 'Netherlands', icon: 'https://flagcdn.com/w40/nl.png' },
        { id: 'united-kingdom', name: 'United Kingdom', icon: 'https://flagcdn.com/w40/gb.png' },
        { id: 'italy', name: 'Italy', icon: 'https://flagcdn.com/w40/it.png' },
        { id: 'spain', name: 'Spain', icon: 'https://flagcdn.com/w40/es.png' },
        { id: 'france', name: 'France', icon: 'https://flagcdn.com/w40/fr.png' },
        { id: 'poland', name: 'Poland', icon: 'https://flagcdn.com/w40/pl.png' },
        { id: 'portugal', name: 'Portugal', icon: 'https://flagcdn.com/w40/pt.png' },
        { id: 'romania', name: 'Romania', icon: 'https://flagcdn.com/w40/ro.png' },
        { id: 'hungary', name: 'Hungary', icon: 'https://flagcdn.com/w40/hu.png' },
        { id: 'canada', name: 'Canada', icon: 'https://flagcdn.com/w40/ca.png' },
        { id: 'united-states', name: 'United States', icon: 'https://flagcdn.com/w40/us.png' },
        { id: 'australia', name: 'Australia', icon: 'https://flagcdn.com/w40/au.png' },
        { id: 'new-zealand', name: 'New Zealand', icon: 'https://flagcdn.com/w40/nz.png' }
    ];


    const jobPositions = [
        { id: 'maid', name: 'Maid', icon: 'https://cdn.helperplace.com/icons/maid.svg' },
        { id: 'driver', name: 'Driver', icon: 'https://cdn.helperplace.com/icons/driver.svg' },
        { id: 'cook', name: 'Cook', icon: 'https://cdn.helperplace.com/icons/maid.svg' },
        { id: 'nanny', name: 'Nanny', icon: 'https://cdn.helperplace.com/icons/nanny.svg' },
        { id: 'nurse', name: 'Nurse', icon: 'https://cdn.helperplace.com/icons/maid.svg' },
        { id: 'houseboy', name: 'Houseboy', icon: 'https://cdn.helperplace.com/icons/maid.svg' },
        { id: 'tailor', name: 'Tailor', icon: 'https://cdn.helperplace.com/icons/maid.svg' },
        { id: 'teacher', name: 'Teacher', icon: 'https://cdn.helperplace.com/icons/maid.svg' },
        { id: 'trainer', name: 'Trainer', icon: 'https://cdn.helperplace.com/icons/maid.svg' },
        { id: 'gardener', name: 'Gardener', icon: 'https://cdn.helperplace.com/icons/maid.svg' },
        { id: 'butler', name: 'Butler', icon: 'https://cdn.helperplace.com/icons/maid.svg' },
        { id: 'housekeeper', name: 'Housekeeper', icon: 'https://cdn.helperplace.com/icons/maid.svg' },
        { id: 'laundry-worker', name: 'Laundry Worker', icon: 'https://cdn.helperplace.com/icons/maid.svg' },
        { id: 'personal-assistant', name: 'Personal Assistant', icon: 'https://cdn.helperplace.com/icons/maid.svg' },
        { id: 'caregiver', name: 'Caregiver', icon: 'https://cdn.helperplace.com/icons/maid.svg' },
        { id: 'elder-care-companion', name: 'Elder Care Companion', icon: 'https://cdn.helperplace.com/icons/maid.svg' },
        { id: 'pet-sitter', name: 'Pet Sitter/Dog Walker', icon: 'https://cdn.helperplace.com/icons/maid.svg' },
        { id: 'house-manager', name: 'House Manager', icon: 'https://cdn.helperplace.com/icons/maid.svg' },
        { id: 'cleaner', name: 'Cleaner', icon: 'https://cdn.helperplace.com/icons/maid.svg' },
        { id: 'maintenance-worker', name: 'Maintenance Worker/Handyman', icon: 'https://cdn.helperplace.com/icons/maid.svg' },
        { id: 'baby-sitter', name: 'Baby Sitter', icon: 'https://cdn.helperplace.com/icons/maid.svg' },
        { id: 'tutor', name: 'Tutor', icon: 'https://cdn.helperplace.com/icons/maid.svg' },
        { id: 'chauffeur', name: 'Chauffeur', icon: 'https://cdn.helperplace.com/icons/driver.svg' },
        { id: 'housemaid', name: 'Housemaid', icon: 'https://cdn.helperplace.com/icons/maid.svg' },
        { id: 'kitchen-staff', name: 'Kitchen Staff', icon: 'https://cdn.helperplace.com/icons/maid.svg' },
        { id: 'dining-room-attendant', name: 'Dining Room Attendant', icon: 'https://cdn.helperplace.com/icons/maid.svg' },
        { id: 'personal-chef', name: 'Personal Chef', icon: 'https://cdn.helperplace.com/icons/maid.svg' },
        { id: 'event-coordinator', name: 'Event Coordinator', icon: 'https://cdn.helperplace.com/icons/maid.svg' },
        { id: 'household-administrator', name: 'Household Administrator', icon: 'https://cdn.helperplace.com/icons/maid.svg' },
        { id: 'domestic-assistant', name: 'Domestic Assistant', icon: 'https://cdn.helperplace.com/icons/maid.svg' }
    ];

    const jobTypes = [
        { id: 'full time', name: 'Full Time', icon: 'https://cdn.helperplace.com/icons/clock.svg' },
        { id: 'part time', name: 'Part Time', icon: 'https://cdn.helperplace.com/icons/time-mgmt.svg' },
        { id: 'temporary', name: 'Temporary', icon: 'https://cdn.helperplace.com/icons/signs.svg' },
        { id: 'permanent', name: 'Permanent', icon: 'https://cdn.helperplace.com/icons/clock.svg' },
        { id: 'internship', name: 'Internship', icon: 'https://cdn.helperplace.com/icons/time-mgmt.svg' },
        { id: 'contractual', name: 'Contractual', icon: 'https://cdn.helperplace.com/icons/clock.svg' },
        { id: 'freelancer', name: 'Freelancer', icon: 'https://cdn.helperplace.com/icons/signs.svg' },
        { id: 'volunteer', name: 'Volunteer', icon: 'https://cdn.helperplace.com/icons/clock.svg' },
        { id: 'fresher', name: 'Fresher', icon: 'https://cdn.helperplace.com/icons/time-mgmt.svg' },
    ];

    const contractStatuses = [
        { id: 'any', name: 'Any' },
        { id: 'contract', name: 'Finshed Contract' },
        { id: 'terminated', name: 'Terminated' },
        { id: 'break contract', name: 'Break Contract' },
        { id: 'transfer', name: 'Transfer' },
        { id: 'Any overseas experience', name: 'Any Overseas Experience' },
        { id: 'resigned', name: 'Resigned' },
    ];

    const postByOptions = [
        { id: 'direct', name: 'Direct' },
        { id: 'agency', name: 'Agency' }
    ];

    const languages = [
        { id: 'english', name: 'English' },
        { id: 'cantonese', name: 'Cantonese' },
        { id: 'mandarin', name: 'Mandarin' },
        { id: 'arabic', name: 'Arabic' },
        { id: 'hindi', name: 'Hindi' },
        { id: 'tagalog', name: 'Tagalog' },
        { id: 'malayali', name: 'Malayali' },
        { id: 'tamil', name: 'Tamil' },
        { id: 'nepali', name: 'Nepali' },
        { id: 'srilankan', name: 'Sri Lankan' },
        { id: 'japanese', name: 'Japanese' },
        { id: 'russian', name: 'Russian' },
        { id: 'bangali', name: 'Bengali' },
        { id: 'vietnamese', name: 'Vietnamese' },
        { id: 'combodian', name: 'Khmer' },
        { id: 'iranian', name: 'Persian' },
        { id: 'korean', name: 'Korean' }
    ];

    const mainSkills = [
        { id: 'babycare', name: 'Baby Care' },
        { id: 'childcare', name: 'Child Care' },
        { id: 'teencare', name: 'Teen Care' },
        { id: 'elderly-care', name: 'Elderly Care' },
        { id: 'pet-care', name: 'Pet Care' },
        { id: 'tutoring', name: 'Tutoring' },
        { id: 'cooking', name: 'Cooking' },
        { id: 'house-keeping', name: 'House-Keeping' },
        { id: 'cleaning', name: 'Cleaning' },
        { id: 'marketing', name: 'Marketing' },
        { id: 'grocerias', name: 'Grocerias' }
    ];

    // Dummy profile images array for fallback
    const dummyProfileImages = [
        'https://cdn-icons-png.flaticon.com/512/3135/3135715.png',
        'https://cdn-icons-png.flaticon.com/512/847/847969.png',
        'https://cdn-icons-png.flaticon.com/512/1077/1077114.png',
        'https://cdn-icons-png.flaticon.com/512/149/149071.png',
        'https://cdn-icons-png.flaticon.com/512/219/219969.png',
        'https://cdn-icons-png.flaticon.com/512/921/921071.png'
    ];

    // Function to get a dummy profile image based on job id or random
    const getDummyProfileImage = (job) => {
        if (job.id) {
            const index = job.id.toString().split('').reduce((sum, char) => sum + char.charCodeAt(0), 0) % dummyProfileImages.length;
            return dummyProfileImages[index];
        }
        return dummyProfileImages[0];
    };

    const toggleFilter = (filterType, id) => {
        setActiveFilters((prev) => {
            const currentValues = prev[filterType] || [];
            const newValues = currentValues.includes(id)
                ? currentValues.filter((item) => item !== id)
                : [...currentValues, id];
            return { ...prev, [filterType]: newValues };
        });
    };

    const toggleRadioFilter = (filterType, id) => {
        setActiveFilters((prev) => ({
            ...prev,
            [filterType]: prev[filterType] === id ? '' : id
        }));
    };

    // Function to reset all filters
    const resetAllFilters = () => {
        setActiveFilters(initialFilters);
        setShowAllLocations(false);
        setShowAllJobTypes(false);
        setShowAllContractStatuses(false);
        setShowAllLanguages(false);
        setShowAllSkills(false);
        setShowAllPositions(false);
    };

    const handleJobClick = (job) => {
        navigate('/viewjobdetails', { state: { job } });
    };

    const stripHTML = (html) => {
        if (!html) return 'No description available';

        // Create a temporary div to parse HTML and get text content
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = html;
        return tempDiv.textContent || tempDiv.innerText || 'No description available';
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';

        try {
            // Handle different date formats
            let date;

            if (dateString.includes('T')) {
                // ISO format with T (e.g., "2025-09-03T15:00:00.000Z")
                date = new Date(dateString);
            } else if (dateString.includes(' ')) {
                // Datetime format without T (e.g., "2025-09-03 15:00:00")
                // Replace space with T to convert to ISO-like format
                date = new Date(dateString.replace(' ', 'T'));
            } else {
                // Date only format (e.g., "2025-09-03")
                date = new Date(dateString + 'T00:00:00');
            }

            // Check if date is valid
            if (isNaN(date.getTime())) {
                return 'N/A';
            }

            return date.toLocaleDateString('en-GB', {
                day: '2-digit',
                month: 'short',
                year: 'numeric'
            });
        } catch (e) {
            console.error('Error formatting date:', e);
            return 'N/A';
        }
    };

    const safeParseArray = (field) => {
        if (Array.isArray(field)) return field;
        if (typeof field === 'string') {
            try {
                const parsed = JSON.parse(field.replace(/\\"/g, '"'));
                return Array.isArray(parsed) ? parsed : [];
            } catch {
                return [];
            }
        }
        return [];
    };

    const parseArrayField = (field) => {
        if (!field) return [];
        if (Array.isArray(field)) return field;
        try {
            let parsed = field;
            while (typeof parsed === 'string') {
                parsed = JSON.parse(parsed);
            }
            return Array.isArray(parsed) ? parsed : [];
        } catch (e) {
            console.error('Error parsing array field:', e);
            return [];
        }
    };

    useEffect(() => {
        const fetchJobData = async () => {
            try {
                if (!userId) return;

                const response = await fetch(`${BaseUrl}/api/employer/by-user?user_id=${userId}`);
                if (!response.ok) throw new Error('Failed to fetch job data');

                let data = await response.json();
                if (!Array.isArray(data)) data = data ? [data] : [];

                // --- robust date parser ---
                const parseDateValue = (val) => {
                    if (val == null) return null;

                    if (typeof val === 'number') {
                        // seconds -> ms, or already ms
                        return val < 1e12 ? val * 1000 : val;
                    }

                    if (typeof val === 'string') {
                        // /Date(123456789)/
                        const m = val.match(/\/Date\((\d+)(?:[+-]\d+)?\)\//);
                        if (m) return Number(m[1]);

                        const parsed = Date.parse(val);
                        if (!Number.isNaN(parsed)) return parsed;

                        const n = Number(val);
                        if (!Number.isNaN(n)) return parseDateValue(n);

                        return null;
                    }

                    if (typeof val === 'object') {
                        // Firestore-like Timestamp objects or objects with helper methods
                        if (typeof val.toDate === 'function') {
                            try { return val.toDate().getTime(); } catch (e) { }
                        }
                        if (typeof val.toMillis === 'function') {
                            try { return val.toMillis(); } catch (e) { }
                        }
                        if (typeof val.seconds === 'number') {
                            return val.seconds * 1000 + Math.floor((val.nanoseconds || 0) / 1e6);
                        }
                        if (typeof val._seconds === 'number') {
                            return val._seconds * 1000 + Math.floor((val._nanoseconds || 0) / 1e6);
                        }
                        if (val.$date) return parseDateValue(val.$date);
                        if (val.$numberLong) return parseDateValue(Number(val.$numberLong));
                        return null;
                    }

                    return null;
                };

                const getTimestampFromJob = (job) => {
                    const keysToTry = [
                        'created_at', 'createdAt', 'posted_at', 'postedAt',
                        'updated_at', 'updatedAt', 'timestamp', 'date', 'created', 'added_at', 'addedAt'
                    ];

                    for (const k of keysToTry) {
                        if (k in job) {
                            const t = parseDateValue(job[k]);
                            if (t !== null) return t;
                        }
                    }

                    // Try Mongo ObjectId timestamp from _id or id (first 8 hex chars -> seconds)
                    const maybeOid = job._id?.$oid || job._id || job.id;
                    if (maybeOid) {
                        if (typeof maybeOid === 'string' && /^[0-9a-fA-F]{24}$/.test(maybeOid)) {
                            try { return parseInt(maybeOid.substring(0, 8), 16) * 1000; } catch (e) { }
                        }
                        const parsed = parseDateValue(maybeOid);
                        if (parsed !== null) return parsed;
                    }

                    return null;
                };
                // --- end parsers ---

                const processedJobs = data.map(job => {
                    const sorted = [...(job.job_history || [])].sort(
                        (a, b) => parseDateValue(b.date) - parseDateValue(a.date)
                    );
                    const latest = sorted.length > 0 ? sorted[0] : null;

                    return {
                        ...job,
                        preferred_candidates_country: parseArrayField(job.preferred_candidates_country),
                        candidates_country_experience: parseArrayField(job.candidates_country_experience),
                        main_skills: parseArrayField(job.main_skills).filter(skill => skill && !['[', ']', '"', '\\'].includes(skill)),
                        cooking_skills: parseArrayField(job.cooking_skills).filter(skill => skill && !['[', ']', '"', '\\'].includes(skill)),
                        other_skills: parseArrayField(job.other_skills).filter(skill => skill && !['[', ']', '"', '\\'].includes(skill)),
                        preferred_years_of_experience: parseArrayField(job.preferred_years_of_experience),
                        offer_for_selected_candidates: parseArrayField(job.offer_for_selected_candidates),
                        preferred_language_for_worker: parseArrayField(job.preferred_language_for_worker),
                        profile_photo_url: job.profile_photo ? `${BaseUrl}${job.profile_photo}` : null,
                        dummy_profile_image: getDummyProfileImage(job),
                        __ts: getTimestampFromJob(job),
                        application_history: safeParseArray(job.application_history),
                        hired_history: safeParseArray(job.hired_history),
                        rejected_history: safeParseArray(job.rejected_history),
                        job_history: sorted,
                        latestHistoryDate: latest ? parseDateValue(latest.date) : 0,
                    };
                });

                const hasAnyTs = processedJobs.some(j => j.__ts !== null && j.__ts !== undefined);

                if (hasAnyTs) {
                    processedJobs.sort((a, b) => (b.__ts || 0) - (a.__ts || 0));
                } else {
                    processedJobs.reverse();
                }

                const finalData = processedJobs.map(({ __ts, ...rest }) => rest);

                setJobs(finalData.sort((a, b) => b.latestHistoryDate - a.latestHistoryDate));
            } catch (err) {
                setError(err?.message || 'Something went wrong');
                console.error('Error fetching jobs:', err);
            } finally {
                setLoading(false);
            }
        };

        const fetchChatConfig = async () => {
            try {
                if (userId) {
                    const subRes = await fetch(`${BaseUrl}/api/employer/subscription/${userId}`);
                    if (subRes.ok) {
                        const data = await subRes.json();
                        setEmployerSubPlan(data.subscription?.plan_type || 'free');
                        setCurrentFeatures(data.features || []);
                    }
                }
                const adminRes = await fetch(`${BaseUrl}/api/admin/settings/allow_free_custom_chat`);
                if (adminRes.ok) {
                    const data = await adminRes.json();
                    setAllowFreeCustomChat(data.is_enabled === 1);
                }
            } catch (err) {
                console.error('Error fetching chat config:', err);
            }
        };

        fetchJobData();
        fetchChatConfig();
    }, [userId]);

    // Apply filters whenever filter criteria change
    useEffect(() => {
        let result = [...jobs];

        // Filter by job position
        if (activeFilters.position) {
            result = result.filter(job => {
                const category = job.domestic_worker_category?.toLowerCase().replace(/\s+/g, '-');
                return category?.includes(activeFilters.position);
            });
        }

        // Filter by location
        if (activeFilters.location.length > 0) {
            result = result.filter(job => {
                const preferredLocations = job.preferred_candidates_country?.map(loc => loc.toLowerCase()) || [];
                return activeFilters.location.some(locationId => {
                    const location = jobLocations.find(loc => loc.id === locationId);
                    if (!location) return false;

                    return preferredLocations.includes(location.name.toLowerCase()) ||
                        job.working_city?.toLowerCase().includes(location.name.toLowerCase()) ||
                        job.state_or_province?.toLowerCase().includes(location.name.toLowerCase());
                });
            });
        }

        // Filter by job type
        if (activeFilters.jobType) {
            result = result.filter(job => {
                const type = job.job_type?.toLowerCase();
                return type?.includes(activeFilters.jobType.toLowerCase());
            });
        }

        // Filter by contract status
        if (activeFilters.contractStatus.length > 0) {
            result = result.filter(job => {
                const status = job.contract_status?.toLowerCase();
                return activeFilters.contractStatus.some(contractStatus =>
                    status?.includes(contractStatus)
                );
            });
        }

        // Filter by posted by
        if (activeFilters.postBy) {
            result = result.filter(job => {
                const type = job.posted_by?.toLowerCase();
                return type?.includes(activeFilters.postBy.toLowerCase());
            });
        }

        // Filter by language
        if (activeFilters.language.length > 0) {
            result = result.filter(job => {
                const jobLanguages = Array.isArray(job.preferred_language_for_worker)
                    ? job.preferred_language_for_worker.map(lang => lang?.toString().toLowerCase())
                    : [];

                return activeFilters.language.some(langId => {
                    const languageObj = languages.find(l => l.id === langId);
                    const languageName = languageObj?.name?.toLowerCase();
                    return languageName && jobLanguages.some(jobLang => jobLang.includes(languageName));
                });
            });
        }

        // Filter by main skills
        if (activeFilters.skills.length > 0) {
            result = result.filter(job => {
                const skills = job.main_skills?.join(' ').toLowerCase() || '';
                return activeFilters.skills.some(skillId => {
                    const skill = mainSkills.find(s => s.id === skillId);
                    return skill && skills.includes(skill.name.toLowerCase());
                });
            });
        }

        // Filter by start date
        if (activeFilters.startDate) {
            try {
                const selectedDate = new Date(activeFilters.startDate);
                selectedDate.setHours(0, 0, 0, 0);

                result = result.filter(job => {
                    if (!job.job_starting_date) return false;

                    try {
                        const jobDate = new Date(job.job_starting_date);
                        jobDate.setHours(0, 0, 0, 0);
                        return jobDate >= selectedDate;
                    } catch (e) {
                        console.error('Error parsing job date:', job.job_starting_date, e);
                        return false;
                    }
                });
            } catch (e) {
                console.error('Error parsing selected date:', activeFilters.startDate, e);
            }
        }

        setFilteredJobs(result);
    }, [jobs, activeFilters]);

    // ── Messages Inbox ────────────────────────────────────────────
    const fetchJobMessages = async (job, silent = false) => {
        if (!silent) setMsgLoading(true);
        try {
            const res = await fetch(`${BaseUrl}/message/by-job/${job.id}`);
            if (!res.ok) throw new Error('Failed to fetch messages');
            const data = await res.json();
            setJobMessages(data);

            // Keep current candidate selected during auto-refresh; fallback to first only if needed
            const candidates = [...new Map(data.map(m => [m.job_seeker_id, m])).values()];
            if (candidates.length > 0) {
                const currentSelectedId = selectedCandidateRef.current?.job_seeker_id;
                const preserved = currentSelectedId
                    ? candidates.find(c => c.job_seeker_id === currentSelectedId)
                    : null;
                const targetCandidate = preserved || candidates[0];
                setSelectedCandidate(targetCandidate);
                await fetchConversationStatus(job.id, targetCandidate.job_seeker_id);
            } else {
                setSelectedCandidate(null);
            }
        } catch (e) {
            console.error(e);
            if (!silent) setJobMessages([]);
        } finally {
            if (!silent) setMsgLoading(false);
        }
    };

    // Polling effect for open message modal
    useEffect(() => {
        let intervalId;
        if (showMsgModal && selectedMsgJob) {
            intervalId = setInterval(() => {
                fetchJobMessages(selectedMsgJob, true); // silent=true: no loading spinner on poll
            }, 15000);
        }
        return () => {
            if (intervalId) clearInterval(intervalId);
        };
    }, [showMsgModal, selectedMsgJob]);

    const fetchConversationStatus = async (jobId, jobSeekerId) => {
        try {
            const statusRes = await fetch(`${BaseUrl}/message/conversation-status?job_id=${jobId}&job_seeker_id=${jobSeekerId}`);
            if (statusRes.ok) {
                const statusData = await statusRes.json();
                setChatStatus(statusData);
            }

            // Also check candidate sub
            const candSubRes = await fetch(`${BaseUrl}/api/candidate/subscription/${jobSeekerId}`);
            if (candSubRes.ok) {
                const candSubData = await candSubRes.json();
                const sub = candSubData.subscription || {};
                const planName = String(sub.plan_type || sub.plan_name || sub.name || '').toLowerCase();
                const isPaid = planName !== '' && !planName.includes('free');
                setCandidateHasSub(sub.status === 'active' && isPaid);
            }
        } catch (e) {
            console.error('Error fetching chat status/candidate sub:', e);
        }
    };

    const handleOpenMessages = (e, job) => {
        e.stopPropagation();

        if (isPendingVerificationJob(job)) {
            Swal.fire('Info', 'Job not verified yet.', 'info');
            return;
        }

        navigate('/employer-messages');
    };

    const handleCloseMsgModal = () => {
        setShowMsgModal(false);
        setSelectedMsgJob(null);
        setSelectedCandidate(null);
        setJobMessages([]);
        setReplyText('');
    };

    const getEmployerGuidedMessage = () => {
        if (chatStatus.isLocked || chatStatus.nextSenderIsCandidate) {
            return ''; // Do not prefill if locked or candidate's turn
        }

        const candidateName = ((selectedCandidate?.first_name || '') + ' ' + (selectedCandidate?.last_name || '')).trim() || 'Candidate';
        if (chatStatus.stepCount === 1) {
            return `Hello ${candidateName},\nThank you for your interest. We have received your message. We will review your profile and contact you if you are shortlisted.`;
        }
        if (chatStatus.stepCount === 3) {
            return `Hello ${candidateName},\nThank you for your patience. Your profile looks suitable. Please share your experience details, expected salary, and availability for interview.`;
        }
        if (chatStatus.stepCount === 5) {
            return `Thank you for the details.\nWe would like to schedule your interview. Please confirm your availability and preferred time.`;
        }
        return '';
    };

    useEffect(() => {
        const enforceGuided = employerSubPlan === 'free' && !allowFreeCustomChat && !candidateHasSub;
        if (!enforceGuided || !selectedCandidate || !selectedMsgJob) return;
        if (chatStatus.isLocked || chatStatus.nextSenderIsCandidate) return;
        const template = getEmployerGuidedMessage();
        if (template && !replyText) setReplyText(template);
    }, [chatStatus, selectedCandidate, selectedMsgJob, employerSubPlan, allowFreeCustomChat, candidateHasSub]);

    const handleReply = async () => {
        if (!replyText.trim() || !selectedCandidate || !selectedMsgJob) return;

        const enforceGuided = employerSubPlan === 'free' && !allowFreeCustomChat && !candidateHasSub;
        if (enforceGuided) {
            if (chatStatus.isLocked) {
                Swal.fire({
                    html: `
                        <div style="text-align:center;padding:10px 0">
                            <div style="font-size:3rem;margin-bottom:12px">📱💬</div>
                            <p style="font-size:1rem;font-weight:600;color:#333;line-height:1.5">
                                You have reached the maximum number of free messages. Upgrade your plan to continue chatting.
                            </p>
                        </div>`,
                    showCancelButton: true,
                    confirmButtonText: '<i class="bi bi-whatsapp"></i> Subscribe Now',
                    cancelButtonText: '<i class="bi bi-chat-left-dots"></i> Wait for Reply',
                    confirmButtonColor: '#28a745',
                    cancelButtonColor: '#6c757d',
                    reverseButtons: false,
                    showCloseButton: true,
                    customClass: {
                        popup: 'subscribe-intercept-popup'
                    }
                }).then((result) => {
                    if (result.isConfirmed) {
                        navigate('/subscriptionplans');
                    }
                });
                return;
            }
            if (chatStatus.nextSenderIsCandidate) {
                Swal.fire({
                    html: `
                        <div style="text-align:center;padding:10px 0">
                            <div style="font-size:3rem;margin-bottom:12px">📱💬</div>
                            <p style="font-size:1rem;font-weight:600;color:#333;line-height:1.5">
                                Wait for the candidates reply, or take a subscription to call directly.
                            </p>
                        </div>`,
                    showCancelButton: true,
                    confirmButtonText: '<i class="bi bi-whatsapp"></i> Subscribe Now',
                    cancelButtonText: '<i class="bi bi-chat-left-dots"></i> Wait for Reply',
                    confirmButtonColor: '#28a745',
                    cancelButtonColor: '#6c757d',
                    reverseButtons: false,
                    showCloseButton: true,
                    customClass: {
                        popup: 'subscribe-intercept-popup'
                    }
                }).then((result) => {
                    if (result.isConfirmed) {
                        navigate('/subscriptionplans');
                    }
                });
                return;
            }
        }

        const messageToSend = enforceGuided ? (getEmployerGuidedMessage() || replyText.trim()) : replyText.trim();
        setReplying(true);
        try {
            const res = await fetch(`${BaseUrl}/message`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    emp_id: userId,
                    job_seeker_id: selectedCandidate.job_seeker_id,
                    job_id: selectedMsgJob.id,
                    sender_id: userId,
                    message: messageToSend,
                    sender_type: 'employer',
                    is_custom_chat: !(employerSubPlan === 'free' && !allowFreeCustomChat && !candidateHasSub)
                })
            });
            if (!res.ok) {
                const errData = await res.json().catch(() => ({}));
                throw new Error(errData.error || 'Reply failed');
            }
            setReplyText('');
            await fetchJobMessages(selectedMsgJob);
        } catch (e) {
            alert(e.message || 'Failed to send reply. Please try again.');
        } finally {
            setReplying(false);
        }
    };

    const markMessagesAsRead = async (jobSeekerId, job) => {
        if (!jobSeekerId || !job) return;
        try {
            await fetch(`${BaseUrl}/message/mark-read`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    emp_id: userId,
                    job_seeker_id: jobSeekerId,
                    job_id: job.id
                })
            });
            // Refresh counts after marking as read
            fetchCounts();
        } catch (e) {
            console.error('Failed to mark as read', e);
        }
    };

    // Effect to mark as read whenever selectedCandidate changes
    useEffect(() => {
        if (showMsgModal && selectedCandidate && selectedMsgJob) {
            markMessagesAsRead(selectedCandidate.job_seeker_id, selectedMsgJob);
        }
    }, [selectedCandidate, showMsgModal]);

    useEffect(() => {
        selectedCandidateRef.current = selectedCandidate;
    }, [selectedCandidate]);

    const fetchCounts = async () => {
        if (!jobs.length) return;
        try {
            const res = await fetch(`${BaseUrl}/message/get-all`);
            if (!res.ok) return;
            const all = await res.json();
            const counts = {};
            jobs.forEach(job => {
                const unreadCandidates = new Set(
                    all.filter(m =>
                        m.job_id === job.id &&
                        m.sender_id === m.job_seeker_id &&
                        Number(m.is_read || 0) === 0
                    ).map(m => m.job_seeker_id)
                );
                counts[job.id] = unreadCandidates.size;
            });
            setMsgCounts(counts);
        } catch (e) { /* silent */ }
    };

    // Fetch unread counts per job on mount
    useEffect(() => {
        fetchCounts();
    }, [jobs]);
    // ─────────────────────────────────────────────────────────────

    const handlePostNewJob = async () => {
        try {
            console.log('Fetching latest temporary_id...');
            const response = await fetch(`${BaseUrl}/api/employer/get/latest-temporary-id`);
            if (!response.ok) {
                console.error('Failed to fetch temporary ID. Status:', response.status);
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            const data = await response.json();
            console.log('API Response:', data);
            const latestTemporaryId = data.latest_temporary_id;
            const newTemporaryId = latestTemporaryId ? Number(latestTemporaryId) + 1 : 1;
            console.log('Navigating to /basicinfo with temporaryId:', newTemporaryId);
            navigate('/basicinfo', { state: { temporaryId: newTemporaryId } });
        } catch (err) {
            console.error('Error in handlePostNewJob:', err);
            navigate('/basicinfo', { state: { temporaryId: 1 } });
        }
    };

    const handleEditJob = (job) => {
        navigate('/basicinfo', {
            state: {
                temporaryId: job.temporary_id,
                isEditing: true,
                jobData: job
            }
        });
    };

    // New: Delete job API handler
    const handleDeleteJob = async (jobId) => {
        setDeleteLoadingId(jobId);
        setDeleteError(null);
        try {
            // API expects DELETE to ${BaseUrl}/api/employer/:id
            const response = await fetch(`${BaseUrl}/api/employer/${jobId}`, {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json'
                }
            });
            if (!response.ok) {
                throw new Error('Failed to delete job');
            }
            // Remove from state
            setJobs(prev => prev.filter(job => job.id !== jobId));
            setFilteredJobs(prev => prev.filter(job => job.id !== jobId));
        } catch (err) {
            setDeleteError('Error deleting job. Try again.');
        } finally {
            setDeleteLoadingId(null);
        }
    };

    const handleRepostJob = async (job, e) => {
        e.stopPropagation();

        if (isPendingVerificationJob(job)) {
            Swal.fire('Info', 'Job not verified yet.', 'info');
            return;
        }

        if (!hasEmployerRepostAccess()) {
            Swal.fire({
                icon: 'info',
                title: 'Upgrade Required',
                text: 'Repost is not enabled in your current employer plan.',
                confirmButtonText: 'View Plans'
            }).then((result) => {
                if (result.isConfirmed) {
                    navigate('/subscriptionplans');
                }
            });
            return;
        }

        const nextAvailableAt = getRepostCooldownEnd(job);
        if (nextAvailableAt && nextAvailableAt > new Date()) {
            Swal.fire(
                'Error',
                `This job is already reposted. It can be reposted again after ${nextAvailableAt.toLocaleString('en-IN')}.`,
                'error'
            );
            return;
        }

        setRepostingJobId(job.id);
        try {
            const response = await fetch(`${BaseUrl}/api/employer/${job.id}/repost`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    requester_id: userId,
                    requester_role: 'employer'
                })
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(
                    data?.next_available_at
                        ? `This job is already reposted. It can be reposted again after ${new Date(data.next_available_at).toLocaleString('en-IN')}.`
                        : data?.error || 'Failed to repost job'
                );
            }

            const updatedFields = {
                reposted_at: data?.job?.reposted_at || new Date().toISOString(),
                last_reposted_at: data?.job?.last_reposted_at || new Date().toISOString()
            };

            setJobs(prev => prev.map(item => (
                item.id === job.id ? { ...item, ...updatedFields } : item
            )));
            setFilteredJobs(prev => prev.map(item => (
                item.id === job.id ? { ...item, ...updatedFields } : item
            )));

            Swal.fire('Success', 'Job reposted successfully.', 'success');
        } catch (error) {
            Swal.fire('Error', error.message || 'Failed to repost job', 'error');
        } finally {
            setRepostingJobId(null);
        }
    };

    if (jobs.length === 0 && !loading) {
        return (
            <>
                <Navbar />

                <div className="container-fluid viewjobposting-empty-wrapper mt-5 py-5">
                    <div className="container">
                        <div className="viewjobposting-empty-card text-center mx-auto">

                            {/* DESKTOP CONTENT */}
                            <div className="viewjobposting-desktop-only">
                                <h2 className="viewjobposting-empty-title">
                                    To view candidate profiles, you must post a job.
                                </h2>

                                <p className="viewjobposting-empty-subtitle">
                                    Post your first job and start receiving applications.
                                </p>

                                <button
                                    className="btn viewjobposting-post-btn"
                                    onClick={handlePostNewJob}
                                >
                                    <span className="viewjobposting-plus">+</span>
                                    <span className="viewjobposting-btn-text">
                                        Post Your First Job
                                    </span>
                                </button>

                                <p className="viewjobposting-empty-footer">
                                    It takes less than 2 minutes to post a job.
                                </p>
                            </div>

                            {/* MOBILE CONTENT */}
                            <div className="viewjobposting-mobile-only">
                                <h2 className="viewjobposting-empty-title">
                                    Post a job to view candidate profiles
                                </h2>

                                <p className="viewjobposting-empty-subtitle">
                                    Start receiving applications instantly
                                </p>

                                <button
                                    className="btn viewjobposting-post-btn"
                                    onClick={handlePostNewJob}
                                >
                                    <span className="viewjobposting-plus">+</span>
                                    <span className="viewjobposting-btn-text">
                                        Post Job
                                    </span>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </>
        );
    }

    if (loading) {
        return (
            <>
                <Navbar />
                <div className="container-fluid viewjobposting-joblisting-wrapper py-4 mt-5">
                    <div className="text-center">Loading jobs...</div>
                </div>
            </>
        );
    }
    if (error) {
        return (
            <>
                <Navbar />
                <div className="container-fluid viewjobposting-joblisting-wrapper py-4 mt-5">
                    <div className="text-center text-danger">Error: {error}</div>
                </div>
            </>
        );
    }

    return (
        <>
            <Navbar />
            <div className="container-fluid viewjobposting-joblisting-wrapper py-4" style={{ marginTop: '80px' }}>
                <div className="row">
                    <div className="col-md-12 mb-3">
                        <div className="employer-viewjobposting-filters p-3 border rounded bg-light">
                            <div className=" employer-viewjobposting-justify-content-between align-items-center">
                                <div className="employer-viewjobposting-header">
                                    <h2>Create and Manage your Job Offers</h2>
                                    <p>Create a new Job Offer and start to receive applications. Easily check, follow and communicate with all your applications.</p>
                                </div>
                                <div className="d-flex flex-row gap-2">
                                    <button className="btn btn-success" onClick={handlePostNewJob}>POST NEW JOB</button>
                                    <button className="btn btn-info" onClick={() => navigate('/candidates')}>FIND CANDIDATE</button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                {/* Mobile Filter Toggle Button */}

                {isMobile && (
                    <div className="mb-2">
                        <button
                            className="btn w-100 text-center"
                            onClick={() => setShowFiltersMobile(!showFiltersMobile)}
                            style={{
                                border: "2px solid #0d6efd",
                                backgroundColor: "#ffffff",
                                color: "#0d6efd",
                                fontWeight: "600",
                                borderRadius: "0px"
                            }}
                        >
                            Filter
                        </button>
                    </div>
                )}

                <div className="row">
                    {/* Filters Column */}
                    {(showFiltersMobile || !isMobile) && (
                        <div className="col-md-3">
                            <div className="viewjobposting-filters p-3 border rounded bg-light">
                                {/* Filter Header with Refresh Button */}
                                <div className="d-flex justify-content-between align-items-center mb-4">
                                    <h5 className="mb-0">Filters</h5>
                                    <button
                                        className="btn btn-sm btn-outline-secondary"
                                        onClick={resetAllFilters}
                                        title="Reset all filters"
                                    >
                                        <i className="bi bi-arrow-clockwise"></i> Reset
                                    </button>
                                </div>

                                {/* Location Filter */}
                                <div className="mb-4 viewjobposting-filter-section">
                                    <div
                                        className="d-flex flex-row justify-content-between align-items-center cursor-pointer mb-2"
                                        onClick={() => setShowLocation(!showLocation)}
                                    >
                                        <strong>Job Location</strong>
                                        <i className={`bi ${showLocation ? 'bi-chevron-down' : 'bi-chevron-right'}`}></i>
                                    </div>
                                    {showLocation && (
                                        <div className="d-flex flex-column gap-2">
                                            {jobLocations.slice(0, showAllLocations ? jobLocations.length : 5).map((loc) => (
                                                <div key={loc.id} className="form-check">
                                                    <input
                                                        className="form-check-input"
                                                        type="checkbox"
                                                        id={`location-${loc.id}`}
                                                        checked={activeFilters.location.includes(loc.id)}
                                                        onChange={() => toggleFilter('location', loc.id)}
                                                    />
                                                    <label className="form-check-label d-flex align-items-center" htmlFor={`location-${loc.id}`}>
                                                        {loc.icon && <img src={loc.icon} alt={loc.name} width="20" height="20" className="me-2" />}
                                                        {loc.name}
                                                    </label>
                                                </div>
                                            ))}
                                            {jobLocations.length > 5 && (
                                                <button
                                                    className="btn btn-link p-0 text-decoration-none viewjobposting-text-primary" style={{ textAlign: 'left' }}
                                                    onClick={() => setShowAllLocations(!showAllLocations)}
                                                >
                                                    {showAllLocations ? 'Show Less' : 'Show More'}
                                                </button>
                                            )}
                                        </div>
                                    )}
                                </div>

                                {/* Job Position Filter */}
                                <div className="mb-4 viewjobposting-filter-section">
                                    <div
                                        className="d-flex flex-row justify-content-between align-items-center cursor-pointer mb-2"
                                        onClick={() => setShowPosition(!showPosition)}
                                    >
                                        <strong>Job Position</strong>
                                        <i className={`bi ${showPosition ? 'bi-chevron-down' : 'bi-chevron-right'}`}></i>
                                    </div>
                                    {showPosition && (
                                        <div className="d-flex flex-column gap-2">
                                            {jobPositions.slice(0, showAllPositions ? jobPositions.length : 5).map((pos) => (
                                                <div key={pos.id} className="form-check">
                                                    <input
                                                        className="form-check-input"
                                                        type="radio"
                                                        name="jobPosition"
                                                        id={`position-${pos.id}`}
                                                        checked={activeFilters.position === pos.id}
                                                        onChange={() => toggleRadioFilter('position', pos.id)}
                                                    />
                                                    <label className="form-check-label d-flex align-items-center" htmlFor={`position-${pos.id}`}>
                                                        {pos.icon && <img src={pos.icon} alt={pos.name} width="20" height="20" className="me-2" />}
                                                        {pos.name}
                                                    </label>
                                                </div>
                                            ))}
                                            {jobPositions.length > 5 && (
                                                <button
                                                    className="btn btn-link p-0 text-decoration-none viewjobposting-text-primary" style={{ textAlign: 'left' }}
                                                    onClick={() => setShowAllPositions(!showAllPositions)}
                                                >
                                                    {showAllPositions ? 'Show Less' : 'Show More'}
                                                </button>
                                            )}
                                        </div>
                                    )}
                                </div>

                                {/* Start Date Filter */}
                                <div className={`mb-4 viewjobposting-filter-section ${showStartDate ? 'active' : ''}`}>
                                    <div
                                        className="d-flex flex-row justify-content-between align-items-center cursor-pointer mb-2"
                                        onClick={() => setShowStartDate(!showStartDate)}
                                    >
                                        <strong>Start Date</strong>
                                        <i className={`bi ${showStartDate ? 'bi-chevron-down' : 'bi-chevron-right'}`}></i>
                                    </div>
                                    {showStartDate && (
                                        <div className="p-2 bg-white rounded border">
                                            <input
                                                type="date"
                                                className="form-control"
                                                value={activeFilters.startDate}
                                                onChange={(e) => setActiveFilters({ ...activeFilters, startDate: e.target.value })}
                                            />
                                        </div>
                                    )}
                                </div>

                                {/* Job Type Filter */}
                                <div className="mb-4 viewjobposting-filter-section">
                                    <div
                                        className="d-flex flex-row justify-content-between align-items-center cursor-pointer mb-2"
                                        onClick={() => setShowJobType(!showJobType)}
                                    >
                                        <strong>Job Type</strong>
                                        <i className={`bi ${showJobType ? 'bi-chevron-down' : 'bi-chevron-right'}`}></i>
                                    </div>
                                    {showJobType && (
                                        <div className="d-flex flex-column gap-2">
                                            {jobTypes.slice(0, showAllJobTypes ? jobTypes.length : 5).map((type) => (
                                                <div key={type.id} className="form-check">
                                                    <input
                                                        className="form-check-input"
                                                        type="radio"
                                                        name="jobType"
                                                        id={`type-${type.id}`}
                                                        checked={activeFilters.jobType === type.id}
                                                        onChange={() => toggleRadioFilter('jobType', type.id)}
                                                    />
                                                    <label className="form-check-label d-flex align-items-center" htmlFor={`type-${type.id}`}>
                                                        {type.icon && <img src={type.icon} alt={type.name} width="20" height="20" className="me-2" />}
                                                        {type.name}
                                                    </label>
                                                </div>
                                            ))}
                                            {jobTypes.length > 5 && (
                                                <button
                                                    className="btn btn-link p-0 text-decoration-none viewjobposting-text-primary" style={{ textAlign: 'left' }}
                                                    onClick={() => setShowAllJobTypes(!showAllJobTypes)}
                                                >
                                                    {showAllJobTypes ? 'Show Less' : 'Show More'}
                                                </button>
                                            )}
                                        </div>
                                    )}
                                </div>

                                {/* Post By Filter - Changed to Radio Buttons */}
                                <div className="mb-4 viewjobposting-filter-section">
                                    <div
                                        className="d-flex flex-row justify-content-between align-items-center cursor-pointer mb-2"
                                        onClick={() => setShowPostBy(!showPostBy)}
                                    >
                                        <strong>Post By</strong>
                                        <i className={`bi ${showPostBy ? 'bi-chevron-down' : 'bi-chevron-right'}`}></i>
                                    </div>
                                    {showPostBy && (
                                        <div className="d-flex flex-column gap-2">
                                            {postByOptions.map((option) => (
                                                <div key={option.id} className="form-check">
                                                    <input
                                                        className="form-check-input"
                                                        type="radio"
                                                        name="postBy"
                                                        id={`postby-${option.id}`}
                                                        checked={activeFilters.postBy === option.id}
                                                        onChange={() => toggleRadioFilter('postBy', option.id)}
                                                    />
                                                    <label className="form-check-label" htmlFor={`postby-${option.id}`}>
                                                        {option.name}
                                                    </label>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>

                                {/* Language Filter */}
                                <div className="mb-4 viewjobposting-filter-section">
                                    <div
                                        className="d-flex flex-row justify-content-between align-items-center cursor-pointer mb-2"
                                        onClick={() => setShowLanguage(!showLanguage)}
                                    >
                                        <strong>Language</strong>
                                        <i className={`bi ${showLanguage ? 'bi-chevron-down' : 'bi-chevron-right'}`}></i>
                                    </div>
                                    {showLanguage && (
                                        <div className="d-flex flex-column gap-2">
                                            {languages.slice(0, showAllLanguages ? languages.length : 5).map((lang) => (
                                                <div key={lang.id} className="form-check">
                                                    <input
                                                        className="form-check-input"
                                                        type="checkbox"
                                                        id={`lang-${lang.id}`}
                                                        checked={activeFilters.language.includes(lang.id)}
                                                        onChange={() => toggleFilter('language', lang.id)}
                                                    />
                                                    <label className="form-check-label" htmlFor={`lang-${lang.id}`}>
                                                        {lang.name}
                                                    </label>
                                                </div>
                                            ))}
                                            {languages.length > 5 && (
                                                <button
                                                    className="btn btn-link p-0 text-decoration-none viewjobposting-text-primary" style={{ textAlign: 'left' }}
                                                    onClick={() => setShowAllLanguages(!showAllLanguages)}
                                                >
                                                    {showAllLanguages ? 'Show Less' : 'Show More'}
                                                </button>
                                            )}
                                        </div>
                                    )}
                                </div>

                                {/* Main Skills Filter */}
                                <div className="mb-4 viewjobposting-filter-section">
                                    <div
                                        className="d-flex flex-row justify-content-between align-items-center cursor-pointer mb-2"
                                        onClick={() => setShowSkills(!showSkills)}
                                    >
                                        <strong>Main Skills</strong>
                                        <i className={`bi ${showSkills ? 'bi-chevron-down' : 'bi-chevron-right'}`}></i>
                                    </div>
                                    {showSkills && (
                                        <div className="d-flex flex-column gap-2">
                                            {mainSkills.slice(0, showAllSkills ? mainSkills.length : 5).map((skill) => (
                                                <div key={skill.id} className="form-check">
                                                    <input
                                                        className="form-check-input"
                                                        type="checkbox"
                                                        id={`skill-${skill.id}`}
                                                        checked={activeFilters.skills.includes(skill.id)}
                                                        onChange={() => toggleFilter('skills', skill.id)}
                                                    />
                                                    <label className="form-check-label" htmlFor={`skill-${skill.id}`}>
                                                        {skill.name}
                                                    </label>
                                                </div>
                                            ))}
                                            {mainSkills.length > 5 && (
                                                <button
                                                    className="btn btn-link p-0 text-decoration-none viewjobposting-text-primary" style={{ textAlign: 'left' }}
                                                    onClick={() => setShowAllSkills(!showAllSkills)}
                                                >
                                                    {showAllSkills ? 'Show Less' : 'Show More'}
                                                </button>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}

                    <div className="col-md-9">
                        {filteredJobs.length === 0 ? (
                            <div className="text-center viewjobposting-no-jobs-message">No jobs match your filters</div>
                        ) : (
                            <div className="row">
                                {filteredJobs.map((job) => (
                                    <div key={job.id} className="col-md-12 mb-4">
                                        <div
                                            className="employer-viewjobposting-jobcard-wrapper d-flex p-3 shadow-sm rounded"
                                            onClick={() => handleJobClick(job)}
                                            style={{ cursor: 'pointer' }}
                                        >
                                            {/* Image Section - UPDATED with fallback */}
                                            <div className="viewjobposting-jobcard-image w-25 text-center">
                                                <div className="viewjobposting-avatar-wrap mb-2">
                                                    <img
                                                        src={job.profile_photo_url || job.dummy_profile_image}
                                                        alt={job.job_title}
                                                        className="img-fluid employer-viewjob-image"
                                                        onError={(e) => {
                                                            // If image fails to load, use dummy profile image
                                                            e.target.src = job.dummy_profile_image || dummyProfileImages[0];
                                                        }}
                                                    />
                                                </div>
                                                <div className="viewjobposting-label-btn-wrapper">
                                                    <div className="viewjobposting-label-btn-wrapper">
                                                        <div className="viewjobposting-jobcard-label-text">
                                                            {job.posted_by?.toLowerCase().includes('direct') ? 'Employer' : 'Agency'}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Content Section */}
                                            <div className="viewjobposting-jobcard-content w-75 ms-3">
                                                <div className="mb-3">
                                                    <h5 className="employer-viewjobposting-jobcard-title mb-0">{job.job_title}</h5>
                                                </div>

                                                <div className="viewjobposting-jobcard-family text-muted mb-1">
                                                    {(() => {
                                                        const parts = [];

                                                        // Add adults and kids part if either exists
                                                        if (job.adults && job.children) {
                                                            parts.push(`${job.adults} adults + ${job.children} kids`);
                                                        } else if (job.adults) {
                                                            parts.push(`${job.adults} adults`);
                                                        } else if (job.children) {
                                                            parts.push(`${job.children} kids`);
                                                        }

                                                        return parts.length > 0 ? parts.join(' | ') : null;
                                                    })()}
                                                </div>
                                                <div className="viewjobposting-jobcard-location text-muted mb-0">
                                                    <i className="bi bi-geo-alt-fill me-1"></i>
                                                    {job.working_city || 'N/A'}, {job.state_or_province || 'N/A'}, {job.country || 'N/A'}
                                                </div>
                                                <div className="viewjobposting-jobcard-description text-muted mb-4">
                                                    {stripHTML(job.job_description)}
                                                </div>
                                                <div className="d-flex flex-wrap align-items-center viewjobposting-gap-3 small mt-1">
                                                    <div className="text-muted me-3">
                                                        <i className="bi bi-person-fill me-1"></i>
                                                        {job.domestic_worker_category || 'N/A'} | {job.job_type || 'N/A'}
                                                    </div>
                                                    <div className="text-muted me-3">
                                                        <i className="bi bi-calendar-event me-1"></i>
                                                        <strong>Joining Date:</strong>&nbsp;
                                                        {
                                                            job.job_starting_date &&
                                                                (job.job_starting_date.toString().toLowerCase().includes('immediate') ||
                                                                    job.job_starting_date.toString().toLowerCase().includes('asap') ||
                                                                    job.job_starting_date.toString().toLowerCase().includes('soon') ||
                                                                    job.job_starting_date.toString().toLowerCase().includes('urgent'))
                                                                ? job.job_starting_date
                                                                : formatDate(job.job_starting_date)
                                                        }
                                                    </div>

                                                    <div className="text-success fw-semibold me-auto">
                                                        <i className="bi me-1"></i>
                                                    </div>
                                                    <div className="d-flex gap-2 align-items-center edit-delete-buttons-viewjobposting">
                                                        <button
                                                            className="btn btn-sm btn-secondary"
                                                            onClick={(e) => e.stopPropagation()}
                                                            disabled
                                                        >
                                                            {isMobile && job.status === "Pending Verification" ? "Pending" : job.status}
                                                        </button>

                                                        {/* Messages Button — Moved Here */}
                                                        <button
                                                            className={`btn btn-sm btn-success d-flex align-items-center ${isMobile ? 'px-2' : 'gap-2 px-3'} position-relative`}
                                                            style={{
                                                                borderRadius: '0px',
                                                                height: '32px',
                                                                whiteSpace: 'nowrap',
                                                                opacity: isPendingVerificationJob(job) ? 0.55 : 1
                                                            }}
                                                            onClick={(e) => handleOpenMessages(e, job)}
                                                        >
                                                            <i className="bi bi-chat-dots"></i>
                                                            {!isMobile && <span>Messages</span>}
                                                            {msgCounts[job.id] > 0 && (
                                                                <span
                                                                    className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger"
                                                                    style={{ fontSize: '0.65rem' }}
                                                                >
                                                                    {msgCounts[job.id]}
                                                                </span>
                                                            )}
                                                        </button>
                                                        <button
                                                            className="btn btn-sm btn-primary"
                                                            style={{
                                                                height: '32px',
                                                                borderRadius: '0px',
                                                                opacity: isPendingVerificationJob(job) || !hasEmployerRepostAccess() || isInRepostCooldown(job) ? 0.55 : 1
                                                            }}
                                                            onClick={(e) => handleRepostJob(job, e)}
                                                            disabled={repostingJobId === job.id}
                                                            title={isPendingVerificationJob(job) ? 'Job not verified yet' : hasEmployerRepostAccess() ? 'Repost job' : 'Upgrade required for repost'}
                                                        >
                                                            <BsArrowRepeat className="action-btn-icon-viewjobposting" />
                                                            <span className="action-btn-text-viewjobposting">
                                                                {repostingJobId === job.id ? 'Reposting...' : isInRepostCooldown(job) ? 'Reposted' : 'Repost'}
                                                            </span>
                                                        </button>
                                                        <button
                                                            className="btn btn-sm btn-primary" style={{ height: '32px', borderRadius: '0px' }}
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleEditJob(job);
                                                            }}
                                                        >
                                                            <BsPencilSquare className="action-btn-icon-viewjobposting" />
                                                            <span className="action-btn-text-viewjobposting">Edit</span>
                                                        </button>
                                                        <button
                                                            className="btn btn-sm btn-danger" style={{ height: '32px', borderRadius: '0px' }}
                                                            disabled={deleteLoadingId === job.id}
                                                            onClick={async (e) => {
                                                                e.stopPropagation();
                                                                if (window.confirm('Are you sure you want to delete this job?')) {
                                                                    await handleDeleteJob(job.id);
                                                                }
                                                            }}
                                                        >
                                                            {deleteLoadingId === job.id ? (
                                                                <span className="d-inline-flex align-items-center">
                                                                    <BsHourglassSplit className="action-btn-icon-viewjobposting" />
                                                                    <span className="action-btn-text-viewjobposting">Deleting...</span>
                                                                </span>
                                                            ) : (
                                                                <span className="d-inline-flex align-items-center">
                                                                    <BsTrash className="action-btn-icon-viewjobposting" />
                                                                    <span className="action-btn-text-viewjobposting">Delete</span>
                                                                </span>
                                                            )}
                                                        </button>
                                                    </div>
                                                </div>
                                                {deleteError && deleteLoadingId === job.id && (
                                                    <div className="text-danger mt-2">{deleteError}</div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>
            <Footer />

            {/* ─── Employer Messages Inbox Modal ─────────────────────── */}
            <Modal show={showMsgModal} onHide={handleCloseMsgModal} size="xl" centered>
                <Modal.Header closeButton style={{ background: '#f8f9fa', borderBottom: '2px solid #dee2e6' }}>
                    <Modal.Title style={{ fontSize: '1.1rem', fontWeight: 700 }}>
                        <i className="bi bi-chat-dots me-2 text-success"></i>
                        Messages — <span style={{ color: '#0d6efd' }}>{selectedMsgJob?.job_title}</span>
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body style={{
                    padding: 0,
                    height: isMobile ? '80vh' : '520px',
                    display: 'flex',
                    flexDirection: isMobile ? 'column' : 'row'
                }}>
                    {msgLoading ? (
                        <div className="d-flex align-items-center justify-content-center w-100">
                            <div className="spinner-border text-success" role="status" />
                            <span className="ms-3 text-muted">Loading messages...</span>
                        </div>
                    ) : (() => {
                        // Unique candidates who messaged this job
                        const candidates = [...new Map(jobMessages.map(m => [m.job_seeker_id, m])).values()];

                        const handleContactClick = (type) => {
                            if (!selectedCandidate) return;

                            // If employer is on free plan, block and show upgrade message
                            if (employerSubPlan === 'free') {
                                Swal.fire({
                                    title: 'Upgrade Required',
                                    text: 'You need an active subscription plan to view candidate contact details.',
                                    icon: 'warning',
                                    showCancelButton: true,
                                    confirmButtonText: 'View Plans',
                                    cancelButtonText: 'Cancel'
                                }).then((result) => {
                                    if (result.isConfirmed) {
                                        navigate('/subscriptionplans');
                                    }
                                });
                                return;
                            }

                            // Find the candidate's last message to get contact info
                            const candLastMsg = jobMessages.filter(m => m.job_seeker_id === selectedCandidate.job_seeker_id).slice(-1)[0];
                            if (!candLastMsg) return;

                            if (type === 'call') {
                                const phoneCode = candLastMsg.cand_phone_code || '';
                                const phone = candLastMsg.cand_phone || '';
                                if (!phone) {
                                    Swal.fire('Not Available', 'Candidate has not provided a phone number.', 'info');
                                    return;
                                }
                                window.location.href = `tel:${phoneCode}${phone}`;

                            } else if (type === 'whatsapp') {
                                const waCode = candLastMsg.cand_whatsapp_code || candLastMsg.cand_phone_code || '';
                                const waNum = candLastMsg.cand_whatsapp || candLastMsg.cand_phone || '';
                                if (!waNum) {
                                    Swal.fire('Not Available', 'Candidate has not provided a WhatsApp number.', 'info');
                                    return;
                                }
                                const fullNum = `${waCode}${waNum}`.replace(/\D/g, '');
                                window.open(`https://wa.me/${fullNum}`, '_blank');
                            }
                        };

                        if (candidates.length === 0) {
                            return (
                                <div className="d-flex flex-column align-items-center justify-content-center w-100 text-muted p-5">
                                    <i className="bi bi-inbox" style={{ fontSize: '3rem', opacity: 0.3 }}></i>
                                    <p className="mt-3">No messages yet for this job posting.</p>
                                </div>
                            );
                        }

                        // Conversation for selected candidate
                        const conversation = selectedCandidate
                            ? jobMessages.filter(m => m.job_seeker_id === selectedCandidate.job_seeker_id)
                            : [];

                        return (
                            <>
                                {/* Left — Candidate List */}
                                <div style={{
                                    width: isMobile ? '100%' : '270px',
                                    minWidth: isMobile ? '100%' : '270px',
                                    borderRight: isMobile ? 'none' : '1px solid #dee2e6',
                                    display: isMobile && selectedCandidate ? 'none' : 'block',
                                    overflowY: 'auto', background: '#fff'
                                }}>
                                    <div style={{
                                        padding: '12px 16px', fontWeight: 600, fontSize: '0.85rem',
                                        color: '#6c757d', background: '#f8f9fa', borderBottom: '1px solid #dee2e6'
                                    }}>
                                        {candidates.length} Candidate{candidates.length !== 1 ? 's' : ''}
                                    </div>
                                    {candidates.map(c => {
                                        const isSelected = selectedCandidate?.job_seeker_id === c.job_seeker_id;
                                        const lastMsg = jobMessages.filter(m => m.job_seeker_id === c.job_seeker_id).slice(-1)[0];
                                        const candidateName = (c.first_name || '') + ' ' + (c.last_name || '') || `Candidate #${c.job_seeker_id}`;
                                        return (
                                            <div
                                                key={c.job_seeker_id}
                                                onClick={async () => {
                                                    setSelectedCandidate(c);
                                                    setReplyText('');
                                                    await fetchConversationStatus(selectedMsgJob.id, c.job_seeker_id);
                                                }}
                                                style={{
                                                    padding: '12px 16px', cursor: 'pointer', borderBottom: '1px solid #f0f0f0',
                                                    background: isSelected ? '#e8f5e9' : '#fff',
                                                    borderLeft: isSelected ? '3px solid #28a745' : '3px solid transparent',
                                                    transition: 'background 0.15s'
                                                }}
                                            >
                                                <div style={{ fontWeight: 600, fontSize: '0.9rem', color: isSelected ? '#155724' : '#212529', display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                    <div style={{
                                                        width: '32px',
                                                        height: '32px',
                                                        borderRadius: '50%',
                                                        background: '#e9ecef',
                                                        overflow: 'hidden',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        border: isSelected ? '1px solid #28a745' : '1px solid #dee2e6'
                                                    }}>
                                                        {c.profile_photo ? (
                                                            <img src={c.profile_photo.includes('http') ? c.profile_photo : `${BaseUrl}${c.profile_photo}`} alt="P" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                                        ) : (
                                                            <i className="bi bi-person text-secondary"></i>
                                                        )}
                                                    </div>
                                                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                        {candidateName.trim() || `Candidate #${c.job_seeker_id}`}
                                                    </span>
                                                </div>
                                                {lastMsg && (
                                                    <div style={{
                                                        fontSize: '0.78rem', color: '#6c757d', marginTop: '3px',
                                                        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'
                                                    }}>
                                                        {lastMsg.sender_id === userId ? '📤 You: ' : '📩 '}
                                                        {lastMsg.message?.substring(0, 50)}{lastMsg.message?.length > 50 ? '...' : ''}
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>

                                {/* Right — Conversation Thread */}
                                <div style={{
                                    flex: 1,
                                    display: isMobile && !selectedCandidate ? 'none' : 'flex',
                                    flexDirection: 'column',
                                    overflow: 'hidden'
                                }}>
                                    {selectedCandidate ? (
                                        <>
                                            {/* Header */}
                                            <div style={{
                                                padding: '10px 16px', background: '#f8f9fa',
                                                borderBottom: '1px solid #dee2e6', fontWeight: 600, fontSize: '0.9rem',
                                                display: 'flex', alignItems: 'center', justifyContent: 'space-between'
                                            }}>
                                                <div className="d-flex align-items-center">
                                                    {isMobile && (
                                                        <button
                                                            className="btn btn-link link-dark p-0 me-3"
                                                            onClick={() => setSelectedCandidate(null)}
                                                        >
                                                            <i className="bi bi-arrow-left fs-5"></i>
                                                        </button>
                                                    )}
                                                    <div style={{
                                                        width: '32px',
                                                        height: '32px',
                                                        borderRadius: '50%',
                                                        background: '#e9ecef',
                                                        overflow: 'hidden',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        marginRight: '10px',
                                                        border: '1px solid #28a745'
                                                    }}>
                                                        {selectedCandidate.profile_photo ? (
                                                            <img src={selectedCandidate.profile_photo.includes('http') ? selectedCandidate.profile_photo : `${BaseUrl}${selectedCandidate.profile_photo}`} alt="P" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                                        ) : (
                                                            <i className="bi bi-person text-success"></i>
                                                        )}
                                                    </div>
                                                    <span>
                                                        {((selectedCandidate.first_name || '') + ' ' + (selectedCandidate.last_name || '')).trim() || `Candidate #${selectedCandidate.job_seeker_id}`}
                                                    </span>
                                                </div>
                                                <div className="d-flex align-items-center gap-2">
                                                    <button
                                                        className="btn btn-light btn-sm rounded-circle d-flex align-items-center justify-content-center"
                                                        style={{ width: '32px', height: '32px', border: '1px solid #dee2e6' }}
                                                        onClick={() => handleContactClick('call')}
                                                        title="Call Candidate"
                                                    >
                                                        <i className="bi bi-telephone-fill text-primary"></i>
                                                    </button>
                                                    <button
                                                        className="btn btn-light btn-sm rounded-circle d-flex align-items-center justify-content-center"
                                                        style={{ width: '32px', height: '32px', border: '1px solid #dee2e6' }}
                                                        onClick={() => handleContactClick('whatsapp')}
                                                        title="WhatsApp Candidate"
                                                    >
                                                        <i className="bi bi-whatsapp text-success"></i>
                                                    </button>
                                                    <a
                                                        href={`/allcandidatesdetails/${selectedCandidate.job_seeker_id}`}
                                                        target="_blank" rel="noreferrer"
                                                        className="btn btn-outline-secondary btn-sm ms-2"
                                                        style={{ fontSize: '0.72rem', padding: '1px 6px' }}
                                                        onClick={e => e.stopPropagation()}
                                                    >
                                                        {isMobile ? 'Profile' : 'View Profile'}
                                                    </a>
                                                </div>
                                            </div>

                                            {/* Messages */}
                                            <div style={{
                                                flex: 1, overflowY: 'auto', padding: '16px', display: 'flex',
                                                flexDirection: 'column', gap: '10px', background: '#fcfcfc'
                                            }}>
                                                {conversation.map((msg, idx) => {
                                                    const isFromMe = msg.sender_id === userId;
                                                    const time = msg.created_at
                                                        ? new Date(msg.created_at).toLocaleString('en-GB', {
                                                            day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit'
                                                        })
                                                        : '';
                                                    return (
                                                        <div key={idx} style={{
                                                            display: 'flex',
                                                            justifyContent: isFromMe ? 'flex-end' : 'flex-start'
                                                        }}>
                                                            <div style={{
                                                                maxWidth: isMobile ? '85%' : '72%',
                                                                padding: '10px 14px', borderRadius: '12px',
                                                                background: isFromMe ? '#d1e7dd' : '#fff',
                                                                border: isFromMe ? '1px solid #badbcc' : '1px solid #dee2e6',
                                                                boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
                                                            }}>
                                                                <div style={{ fontSize: '0.85rem', whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>
                                                                    {msg.message}
                                                                </div>
                                                                <div style={{ fontSize: '0.7rem', color: '#999', marginTop: '4px', textAlign: 'right' }}>
                                                                    {isFromMe ? '📤' : '📩'} {time}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>

                                            {/* Reply Box */}
                                            <div style={{ padding: '12px 16px', borderTop: '1px solid #dee2e6', background: '#fff' }}>
                                                <div className="d-flex flex-column gap-2 mb-2">
                                                    {employerSubPlan === 'free' && candidateHasSub && (
                                                        <div className="alert alert-info py-1 px-2 mb-1 text-center" style={{ fontSize: '0.75rem', border: 'none', borderRadius: '4px' }}>
                                                            <i className="bi bi-info-circle me-1"></i> Custom chat unlocked by candidate's subscription
                                                        </div>
                                                    )}
                                                </div>

                                                {(employerSubPlan === 'free' && !allowFreeCustomChat && !candidateHasSub) ? (
                                                    // Guided Chat Reply Logic
                                                    <>
                                                        {chatStatus.isLocked || chatStatus.nextSenderIsCandidate ? (
                                                            <div>
                                                                <div className="d-flex gap-2">
                                                                    <textarea
                                                                        className="form-control form-control-sm"
                                                                        rows={2}
                                                                        placeholder={chatStatus.isLocked ? 'Type a message...' : 'Type a message... (waiting for candidate reply)'}
                                                                        value={replyText}
                                                                        onChange={e => setReplyText(e.target.value)}
                                                                        onKeyDown={e => {
                                                                            if (e.key === 'Enter' && !e.shiftKey) {
                                                                                e.preventDefault();
                                                                                handleReply();
                                                                            }
                                                                        }}
                                                                        disabled={replying}
                                                                        style={{ resize: 'none', fontSize: '0.875rem' }}
                                                                    />
                                                                    <button
                                                                        className="btn btn-success btn-sm px-3"
                                                                        onClick={handleReply}
                                                                        disabled={replying || !replyText.trim()}
                                                                        style={{ whiteSpace: 'nowrap', alignSelf: 'flex-end' }}
                                                                    >
                                                                        {replying
                                                                            ? <span className="spinner-border spinner-border-sm" />
                                                                            : <i className="bi bi-send"></i>}
                                                                    </button>
                                                                </div>
                                                                <small className="text-muted mt-1 d-block">
                                                                    <i className="bi bi-info-circle me-1"></i>
                                                                    Sending a custom message requires a subscription. {chatStatus.isLocked ? 'Upgrade your plan to continue chatting freely.' : 'Wait for candidate reply for next guided message.'}
                                                                </small>
                                                            </div>
                                                        ) : (
                                                            <div className="d-flex flex-column gap-2">
                                                                <p className="mb-1 text-muted" style={{ fontSize: '0.9rem' }}>Send your response:</p>
                                                                <div className="p-2 border rounded bg-light" style={{ fontSize: '0.9rem', whiteSpace: 'pre-wrap' }}>
                                                                    {chatStatus.stepCount === 1 && `Hello ${((selectedCandidate?.first_name || '') + ' ' + (selectedCandidate?.last_name || '')).trim() || 'Candidate'},\nThank you for your interest. We have received your message. We will review your profile and contact you if you are shortlisted.`}
                                                                    {chatStatus.stepCount === 3 && `Hello ${((selectedCandidate?.first_name || '') + ' ' + (selectedCandidate?.last_name || '')).trim() || 'Candidate'},\nThank you for your patience. Your profile looks suitable. Please share your experience details, expected salary, and availability for interview.`}
                                                                    {chatStatus.stepCount === 5 && "Thank you for the details.\nWe would like to schedule your interview. Please confirm your availability and preferred time."}
                                                                </div>
                                                                <button
                                                                    className="btn btn-primary btn-sm align-self-end mt-2"
                                                                    onClick={async () => {
                                                                        let msg = "";
                                                                        const candidateName = ((selectedCandidate?.first_name || '') + ' ' + (selectedCandidate?.last_name || '')).trim() || 'Candidate';
                                                                        if (chatStatus.stepCount === 1) msg = `Hello ${candidateName},\nThank you for your interest. We have received your message. We will review your profile and contact you if you are shortlisted.`;
                                                                        if (chatStatus.stepCount === 3) msg = `Hello ${candidateName},\nThank you for your patience. Your profile looks suitable. Please share your experience details, expected salary, and availability for interview.`;
                                                                        if (chatStatus.stepCount === 5) msg = `Thank you for the details.\nWe would like to schedule your interview. Please confirm your availability and preferred time.`;

                                                                        setReplying(true);
                                                                        try {
                                                                            const res = await fetch(`${BaseUrl}/message`, {
                                                                                method: 'POST',
                                                                                headers: { 'Content-Type': 'application/json' },
                                                                                body: JSON.stringify({
                                                                                    emp_id: userId,
                                                                                    job_seeker_id: selectedCandidate.job_seeker_id,
                                                                                    job_id: selectedMsgJob.id,
                                                                                    sender_id: userId,
                                                                                    message: msg,
                                                                                    sender_type: 'employer'
                                                                                })
                                                                            });
                                                                            if (!res.ok) throw new Error('Reply failed');
                                                                            await fetchJobMessages(selectedMsgJob);
                                                                            await fetchConversationStatus(selectedMsgJob.id, selectedCandidate.job_seeker_id);
                                                                        } catch (e) {
                                                                            alert('Failed to send reply. Please try again.');
                                                                        } finally {
                                                                            setReplying(false);
                                                                        }
                                                                    }}
                                                                    disabled={replying}
                                                                >
                                                                    {replying ? 'Sending...' : 'Send This Reply'} <i className="bi bi-send ms-1"></i>
                                                                </button>
                                                            </div>
                                                        )}
                                                    </>
                                                ) : (
                                                    // Full Custom Chat Logic (Paid or Admin override)
                                                    <div className="d-flex gap-2">
                                                        <textarea
                                                            className="form-control form-control-sm"
                                                            rows={2}
                                                            placeholder="Type your reply..."
                                                            value={replyText}
                                                            onChange={e => setReplyText(e.target.value)}
                                                            onKeyDown={e => {
                                                                if (e.key === 'Enter' && !e.shiftKey) {
                                                                    e.preventDefault();
                                                                    handleReply();
                                                                }
                                                            }}
                                                            disabled={replying}
                                                            style={{ resize: 'none', fontSize: '0.875rem' }}
                                                        />
                                                        <button
                                                            className="btn btn-success btn-sm px-3"
                                                            onClick={handleReply}
                                                            disabled={replying || !replyText.trim()}
                                                            style={{ whiteSpace: 'nowrap', alignSelf: 'flex-end' }}
                                                        >
                                                            {replying
                                                                ? <span className="spinner-border spinner-border-sm" />
                                                                : <i className="bi bi-send"></i>}
                                                        </button>
                                                    </div>
                                                )}
                                                <div style={{ fontSize: '0.65rem', color: '#aaa', marginTop: '4px' }}>
                                                    {isMobile ? 'Enter to send' : 'Press Enter to send, Shift+Enter for new line'}
                                                </div>
                                            </div>
                                        </>
                                    ) : (
                                        <div className="d-flex align-items-center justify-content-center h-100 text-muted p-4 text-center">
                                            Select a candidate to view the conversation
                                        </div>
                                    )}
                                </div>
                            </>
                        );
                    })()}
                </Modal.Body>
            </Modal>
            {/* ────────────────────────────────────────────────────────── */}
        </>
    );
}

export default ViewJobs;
